import { FoodPreference, ProgramType } from 'fitbeat.models';
import { URL, URLSearchParams } from 'url';
import { ContentType } from './ContentType';
import { IContentClient } from './IContentClient';
import { IPost } from './Post';

interface IWordpressPost {
  featured_media: number;
  date: string;
  link: string;
  slug: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  categories: number[];
  tags: number[];
  acf?: {
    app_video_popup?: number;
  };
  menu_order: number;
}

interface IWordpressMediaItem {
  id: number;
  media_details: {
    sizes: IImageSizes;
  };
  source_url: string;
  code?: string;
}

interface IImageSizes {
  thumbnail: {
    source_url: string;
  };
  medium: {
    source_url: string;
  };
  full: {
    source_url: string;
  };
}

interface IWordpressTag {
  id: number;
  name: string;
}

interface IWordpressCategory {
  id: number;
  name: string;
}

interface IWordpressClientConfig {
  wordpressContentCMSUrl: string;
  cloudinaryWordpressAutoUploadUrl: string;
}

// use `Fitillion_grey-bg` as fallback
// in the future this can change to an organization-specific config
const FALLBACK_IMAGE_SIZES_FOR_POST_WITHOUT_IMAGE: IImageSizes = {
  thumbnail: {
    source_url: 'https://fitillion.com/wp-content/uploads/2023/11/Fitillion_grey-bg.svg',
  },
  medium: {
    source_url: 'https://fitillion.com/wp-content/uploads/2023/11/Fitillion_grey-bg.svg',
  },
  full: {
    source_url: 'https://fitillion.com/wp-content/uploads/2023/11/Fitillion_grey-bg.svg',
  },
};

export class WordpressContentClient implements IContentClient {
  private static contentCache = new Map<string, IPost[]>();

  private typeToTagIdMap = {};

  private goalToTagIdMap = {};

  private foodPreferenceToTagIdMap = {};

  private avialableCategories: IWordpressCategory[] = [];

  constructor(
    private config: IWordpressClientConfig,
    private logger: any,
    private fetch: any,
  ) { }

  public async initialize(): Promise<void> {
    await this.setupTypeMappings();
    await this.populateCategories();
  }

  public async getAllAvailableContentFor(
    contentType: ContentType,
    customPostType = 'blog',
  ): Promise<IPost[]> {
    const cachedPosts = this.getPostsFromCache(contentType, customPostType);
    if (!cachedPosts) {
      return this.loadContentAndSetCacheAsync(contentType, customPostType);
    }

    this.loadContentAndSetCacheInBackground(contentType, customPostType);
    return cachedPosts;
  }

  private loadContentAndSetCacheInBackground(contentType: ContentType, customPostType: string) {
    this.loadContentAndSetCacheAsync(contentType, customPostType)
      .catch((e) => this.logger.warn('loadContentAndSetCacheAsync failed', e));
  }

  private async loadContentAndSetCacheAsync(contentType: ContentType, customPostType: string) {
    const postsFromWordpress = await this.loadContentFromWordpress(contentType, customPostType);

    WordpressContentClient.contentCache.set(this.cacheKey(contentType, customPostType), postsFromWordpress);
    return postsFromWordpress;
  }

  private async loadContentFromWordpress(contentType: ContentType, customPostType: string) {
    const contentTypeTagId = this.typeToTagIdMap[contentType] as number;
    const maxNumberOfResults = 100;
    const url = new URL(`/wp-json/wp/v2/${customPostType}`, this.config.wordpressContentCMSUrl);
    const searchParams = {
      status: 'publish',
      per_page: maxNumberOfResults.toString(),
      order: 'asc',
    };
    if (contentTypeTagId) {
      const searchParamsWithTag = {
        ...searchParams,
        tags: contentTypeTagId.toString(),
      };
      url.search = new URLSearchParams(searchParamsWithTag).toString();
    } else {
      url.search = new URLSearchParams(searchParams).toString();
    }

    const response = await this.fetch(url, {
      method: 'GET',
    });
    const posts: IWordpressPost[] = await response.json();
    const sortedByMenuOrder = posts.sort((a, b) => a.menu_order - b.menu_order);
    const mappedPosts = await this.resolveMediaAndMapPosts(
      sortedByMenuOrder,
      contentType,
    );

    return mappedPosts;
  }

  private async resolveMediaAndMapPosts(
    posts: IWordpressPost[],
    contentType: ContentType,
  ): Promise<IPost[]> {
    const mappedPosts = posts.map(
      async (post: IWordpressPost): Promise<IPost> => {
        const imageSource = await this.getImageUrlFor(post.featured_media);
        const videoUrl = await this.getVideoUrl(post);
        const resolvedCategories = [...this.avialableCategories].filter(
          (category) => post.categories.indexOf(category.id) !== -1,
        );
        return {
          publishAt: new Date(post.date),
          featuredImage: imageSource.full.source_url,
          thumbnailImage: imageSource.thumbnail.source_url,
          title: post.title.rendered,
          type: contentType,
          url: post.link,
          content: post.content.rendered,
          slug: post.slug,
          categories: resolvedCategories.map((category) => category.name),
          longTermGoals: this.getLongTermGoals(post),
          foodPreferences: this.getFoodPreferences(post),
          videoUrl,
        };
      },
    );
    return Promise.all(mappedPosts);
  }

  private async getVideoUrl(post: IWordpressPost) {
    if (!post.acf || !post.acf.app_video_popup) {
      return;
    }
    const mediaResult: IWordpressMediaItem = await this.getMediaItem(post.acf.app_video_popup);
    return mediaResult.source_url;
    // Temporarily remove as retrieving content from cloudinary is causing issues on the Elite device
    // const currentUrl = new URL(mediaResult.source_url);
    // return `${this.config.cloudinaryWordpressAutoUploadUrl}${currentUrl.pathname}`;
  }

  private async getImageUrlFor(mediaId: number): Promise<IImageSizes> {
    const mediaResult: IWordpressMediaItem = await this.getMediaItem(mediaId);
    if (mediaResult.code) {
      return FALLBACK_IMAGE_SIZES_FOR_POST_WITHOUT_IMAGE;
    }
    return mediaResult.media_details.sizes;
  }

  private async getMediaItem(mediaId: number) {
    const url = new URL(`/wp-json/wp/v2/media/${mediaId}`, this.config.wordpressContentCMSUrl);
    const response = await this.fetch(url, {
      method: 'GET',
    });
    const mediaResult: IWordpressMediaItem = await response.json();
    return mediaResult;
  }

  private async populateCategories() {
    try {
      const url = new URL('/wp-json/wp/v2/categories', this.config.wordpressContentCMSUrl);
      const response = await this.fetch(url, {
        method: 'GET',
      });
      this.avialableCategories = await response.json();
    } catch (e) {
      this.logger.error(e);
    }
  }

  private async setupTypeMappings() {
    try {
      const url = new URL('/wp-json/wp/v2/tags', this.config.wordpressContentCMSUrl);
      const response = await this.fetch(url, {
        method: 'GET',
      });
      const tags: IWordpressTag[] = await response.json();
      tags.map((tag: IWordpressTag) => {
        this.mapContentTypeToTag(tag);
        this.mapProgramTypeToTag(tag);
        this.mapFoodPreferenceToTag(tag);
      });
    } catch (e) {
      this.logger.error(e);
    }
  }

  private mapProgramTypeToTag(tag: IWordpressTag) {
    Object.keys(ProgramType).forEach((programTypeString) => {
      if (tag.name.toLowerCase() === programTypeString.toLowerCase()) {
        this.goalToTagIdMap[ProgramType[programTypeString]] = tag.id;
      }
    });
  }

  private mapContentTypeToTag(tag: IWordpressTag) {
    Object.keys(ContentType).forEach((contentItemString) => {
      if (tag.name.toLowerCase() === contentItemString.toLowerCase()) {
        this.typeToTagIdMap[ContentType[contentItemString]] = tag.id;
      }
    });
  }

  private mapFoodPreferenceToTag(tag: IWordpressTag) {
    Object.keys(FoodPreference).forEach((foodPreferenceItemString) => {
      if (tag.name.toLowerCase() === foodPreferenceItemString.toLowerCase()) {
        this.foodPreferenceToTagIdMap[FoodPreference[foodPreferenceItemString]] = tag.id;
      }
    });
  }

  private getLongTermGoals(post: IWordpressPost): ProgramType[] {
    const longTermGoals: ProgramType[] = [];
    Object.keys(this.goalToTagIdMap).forEach((key: string) => {
      const enumValue = this.goalToTagIdMap[key];
      if (post.tags.some((tagId) => tagId === enumValue)) {
        longTermGoals.push(key as ProgramType);
      }
    });

    return longTermGoals;
  }

  private getFoodPreferences(post: IWordpressPost): FoodPreference[] {
    const foodPreferences: FoodPreference[] = [];
    Object.keys(this.foodPreferenceToTagIdMap).forEach((key: string) => {
      const enumValue = this.foodPreferenceToTagIdMap[key];
      if (post.tags.some((tagId) => tagId === enumValue)) {
        foodPreferences.push(key as FoodPreference);
      }
    });

    return foodPreferences;
  }

  private getPostsFromCache(contentType: ContentType, customPostType: string): IPost[] | undefined {
    return WordpressContentClient.contentCache.get(this.cacheKey(contentType, customPostType));
  }

  private cacheKey(contentType: ContentType, customPostType: string): string {
    return contentType + customPostType;
  }
}
